/**
 * 门户语言路径前缀：中文无前缀，其它语言用两字母语言段（en-US → /en，fr-FR → /fr）。
 * 不依赖 menuPath / navMenu，避免循环引用。
 */

export const DEFAULT_LOCALE_LANGUAGE = 'zh'
export const LOCALE_PREFIX_PATTERN = '[a-z]{2}'

const HOME = '/'
const PREFIX_SEGMENT = /^[a-z]{2}$/

function normalizePath(path) {
  if (!path) return ''
  const trimmed = String(path).trim()
  if (!trimmed || trimmed === HOME) return HOME
  const withSlash = trimmed.startsWith('/') ? trimmed : `/${trimmed}`
  if (withSlash === HOME) return HOME
  return withSlash.replace(/\/+$/, '') || HOME
}

export function languagePart(localeCode) {
  return String(localeCode || '').split(/[-_]/)[0].toLowerCase()
}

export function isLocalePathPrefix(segment) {
  const s = String(segment || '').toLowerCase()
  return PREFIX_SEGMENT.test(s) && s !== DEFAULT_LOCALE_LANGUAGE
}

export function localeToPrefix(localeCode) {
  const lang = languagePart(localeCode)
  if (!isLocalePathPrefix(lang)) return ''
  return lang
}

export function parseLocalePrefix(path) {
  const normalized = normalizePath(path)
  if (!normalized || normalized === HOME) return ''
  const first = (normalized.slice(1).split('/')[0] || '').toLowerCase()
  return isLocalePathPrefix(first) ? first : ''
}

export function findDefaultUnprefixedLocale(enabledLocales = []) {
  const locales = (enabledLocales || []).filter((item) => item?.code)
  const zh = locales.find((item) => languagePart(item.code) === DEFAULT_LOCALE_LANGUAGE)
  if (zh) return zh.code
  const flagged = locales.find((item) => item.isDefault)
  if (flagged && !localeToPrefix(flagged.code)) return flagged.code
  const unprefixed = locales.find((item) => !localeToPrefix(item.code))
  return unprefixed?.code || locales[0]?.code || ''
}

export function prefixToLocale(prefix, enabledLocales = []) {
  const p = String(prefix || '').toLowerCase()
  const locales = (enabledLocales || []).filter((item) => item?.code)
  if (!p) return findDefaultUnprefixedLocale(locales)
  return locales.find((item) => languagePart(item.code) === p)?.code || ''
}

export function stripLocalePrefix(path) {
  const normalized = normalizePath(path)
  const prefix = parseLocalePrefix(normalized)
  if (!prefix) return normalized || HOME
  const rest = normalized.slice(prefix.length + 1)
  return rest ? normalizePath(rest) : HOME
}

export function withLocalePrefix(contentPath, localeCode) {
  const normalized = normalizePath(contentPath)
  const prefix = localeToPrefix(localeCode)
  if (prefix) {
    const head = `/${prefix}`
    if (normalized === head || normalized.startsWith(`${head}/`)) {
      return normalized
    }
  }
  const stripped = stripLocalePrefix(normalized)
  if (!prefix) return stripped || HOME
  if (!stripped || stripped === HOME) return `/${prefix}`
  return `/${prefix}${stripped}`
}

export function isLocaleHomePath(path) {
  const normalized = normalizePath(path)
  if (!normalized || normalized === HOME) return true
  if (normalized.indexOf('/', 1) >= 0) return false
  return isLocalePathPrefix(normalized.slice(1))
}

export function getRouteContentPath(route) {
  if (!route) return HOME
  if (route.meta?.isHome || route.name === 'Home') return HOME
  return stripLocalePrefix(route.path)
}

export function withLocaleRouteParams(params, localeCode) {
  const next = { ...(params || {}) }
  const prefix = localeToPrefix(localeCode)
  if (prefix) next.localePrefix = prefix
  else delete next.localePrefix
  return next
}

export function dropLangQuery(query = {}) {
  if (!query || typeof query !== 'object') return { ...query }
  const next = { ...query }
  delete next.lang
  return next
}

export function readLangQuery(query) {
  if (!query || typeof query !== 'object') return ''
  return String(query.lang || '').trim()
}

export function localizeHref(path, localeCode) {
  const raw = String(path || '').trim()
  if (!raw) return path
  if (/^(https?:|mailto:|tel:|#)/i.test(raw)) return path
  return withLocalePrefix(raw, localeCode)
}

export function localizeRouteLocation(to, localeCode) {
  if (to == null || to === '') return to
  if (typeof to === 'string') return localizeHref(to, localeCode)
  if (typeof to !== 'object') return to
  if (to.name) {
    return {
      ...to,
      params: withLocaleRouteParams(to.params || {}, localeCode)
    }
  }
  if (to.path) {
    return { ...to, path: localizeHref(to.path, localeCode) }
  }
  return to
}
